import React, { useState, useEffect, useRef } from 'react';
import styles from './Paginator.module.css';
import { Paginator } from 'primereact/paginator';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';

const AIEduPaginator = ({ rows, first, onPageChange, totalRecords }) => {
    const [isOverlapping, setIsOverlapping] = useState(false);
    const paginatorRef = useRef(null);
    const debounceTimerRef = useRef(null);
    const lastStateRef = useRef(false);

    // Check if paginator overlaps with any datatable
    const checkOverlap = () => {
        if (!paginatorRef.current) return;
        
        const viewportHeight = window.innerHeight;

        // Find all datatables in the page
        const datatables = document.querySelectorAll('.p-datatable-wrapper, .p-datatable');
        
        let hasOverlap = false;
        let maxTableBottom = 0;
        
        datatables.forEach(table => {
            const tableRect = table.getBoundingClientRect();
            const tableBottom = tableRect.bottom;
            maxTableBottom = Math.max(maxTableBottom, tableBottom);
            
            // Check if table extends into the bottom area where paginator would be
            // Paginator is fixed at bottom, so check if table goes into that area
            const paginatorArea = viewportHeight - 100; // Bottom 100px area
            if (tableBottom > paginatorArea) {
                hasOverlap = true;
            }
        });

        // Only update state if the overlap status has actually changed
        if (lastStateRef.current !== hasOverlap) {
            lastStateRef.current = hasOverlap;
            setIsOverlapping(hasOverlap);
        }
    };

    // Debounced version of checkOverlap to prevent rapid state changes
    const debouncedCheckOverlap = () => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        
        debounceTimerRef.current = setTimeout(() => {
            checkOverlap();
        }, 150); // Reduced debounce for better responsiveness
    };

    useEffect(() => {
        // Initial check
        checkOverlap();

        // Check on window resize and zoom
        const handleResize = () => {
            debouncedCheckOverlap();
        };

        // Handle zoom events more efficiently
        let zoomTimeout;
        const handleZoom = (e) => {
            if (e.ctrlKey) {
                if (zoomTimeout) {
                    clearTimeout(zoomTimeout);
                }
                zoomTimeout = setTimeout(() => {
                    checkOverlap();
                }, 100);
            }
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('wheel', handleZoom, { passive: true });

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('wheel', handleZoom);
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            if (zoomTimeout) {
                clearTimeout(zoomTimeout);
            }
        };
    }, []);
    
    const paginatorTemplate = {
        layout: 'RowsPerPageDropdown PrevPageLink CurrentPageReport NextPageLink JumpToPageInput',
        RowsPerPageDropdown: (options) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 20, value: 20 },
                { label: 50, value: 50 },
                { label: 100, value: 100 },
            ];

            return (
                <React.Fragment>
                    <span className={styles.optionsLabelStyle}>
                        Rows per page:{' '}
                    </span>
                    <Dropdown
                        className={styles.paginator_dropdown}
                        value={options.value}
                        options={dropdownOptions}
                        onChange={options.onChange}
                    />
                </React.Fragment>
            );
        },

        JumpToPageInput: (options) => {
            const { value, props, onChange } = options;
            const [inputValue, setInputValue] = useState(value);

            // @ts-ignore - totalPages is not defined in PaginatorJumpToPageInputOptions
            const totalPages = props.totalPages || 0;

            const handleInputChange = (e) => {
                if (!e.value) return;
                const newPage = Math.max(1, Math.min(e.value, totalPages));
                setInputValue(newPage);
            };

            const handleKeyDown = (e) => {
                if (e.key === 'Enter') {
                    // @ts-ignore
                    onChange({
                        originalEvent: e,
                        value: inputValue,
                    });
                }
            };

            return (
                <InputNumber
                    value={inputValue}
                    defaultValue={value}
                    placeholder='#'
                    min={1}
                    max={totalPages}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    tooltip={'Press Enter to go to page'}
                    tooltipOptions={{ position: 'top', showDelay: 200 }}
                    inputClassName={styles.pageInput}
                />
            );
        },
    };

    return (
        <div 
            ref={paginatorRef}
            className={`${styles.custom_paginatorStyle} ${isOverlapping ? styles.relative : ''}`}
        >
            <Paginator
                template={paginatorTemplate}
                first={first}
                rows={rows}
                totalRecords={totalRecords}
                onPageChange={onPageChange}
            />
        </div>
    );
};

export default AIEduPaginator;
